import React, {useState, useEffect, useRef} from 'react';
import Card from '../../../Presentation/components/ui/Card';
import Button from '../../../Presentation/components/ui/Button';
import Spinner from '../../../Presentation/components/ui/Spinner';
import {AppText, AppBodyText, AppLabelText, AppSectionTitle, AppErrorText} from '../../../Presentation/components/ui/Typography';
import ConfirmationDialog from '../../../Presentation/components/ui/ConfirmationDialog';
import {useNotification} from '../../../Services/useNotification';
import {useSalesDashboard} from '../hooks/useSalesDashboard';
import {FileText, Upload, Download, Trash2, RefreshCw, AlertCircle} from 'lucide-react';
import {formatDate} from '../../../Presentation/utils/dateUtils';

const NO_ATTACHMENT_MESSAGE_REGEX = /لا يوجد.*مرفق|لا يوجد ملف مرفق/i;

function isNoAttachmentError (message: string | undefined): boolean {
  return typeof message === 'string' && NO_ATTACHMENT_MESSAGE_REGEX.test(message);
}

function isNoAttachmentResponse (err: { statusCode?: number; message?: string } | undefined): boolean {
  if (!err) return false;
  if (err.statusCode === 404) return true;
  return isNoAttachmentError(err.message);
}

interface InvoiceAttachmentCardProps {
  invoiceId: string;
}

const InvoiceAttachmentCard: React.FC<InvoiceAttachmentCardProps> = ({invoiceId}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {success, error: showError} = useNotification();
  
  const {
    invoiceAttachment,
    attachmentInvoiceId,
    attachmentLoading,
    attachmentError,
    fetchInvoiceAttachment,
    refreshInvoiceAttachment,
    uploadInvoiceAttachment,
    replaceInvoiceAttachment,
    deleteInvoiceAttachment
  } = useSalesDashboard();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    if (!invoiceId) return;
    
    const load = async () => {
      try {
        await fetchInvoiceAttachment(invoiceId);
      } catch (err: unknown) {
        if (!isNoAttachmentResponse(err as { statusCode?: number; message?: string })) {
          showError((err as Error)?.message || 'حدث خطأ في تحميل معلومات المرفق');
        }
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showError('يرجى اختيار ملف PDF فقط');
      return;
    }

    if (invoiceAttachment) {
      handleReplace(file);
    } else {
      handleUpload(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const refreshAfterUpload = async () => {
    const attachment = await refreshInvoiceAttachment(invoiceId);
    if (!attachment) {
      await new Promise((r) => setTimeout(r, 800));
      await refreshInvoiceAttachment(invoiceId);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      await uploadInvoiceAttachment(invoiceId, file);
      success('تم رفع المرفق بنجاح');
      await refreshAfterUpload();
    } catch (err: any) {
      showError(err?.message || 'حدث خطأ في رفع المرفق');
    }
  };

  const handleReplace = async (file: File) => {
    try {
      await replaceInvoiceAttachment(invoiceId, file);
      success('تم استبدال المرفق بنجاح');
      await refreshAfterUpload();
    } catch (err: any) {
      showError(err?.message || 'حدث خطأ في استبدال المرفق');
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      await deleteInvoiceAttachment(invoiceId);
      success('تم حذف المرفق بنجاح');
    } catch (err: any) {
      showError(err?.message || 'حدث خطأ في حذف المرفق');
    }
  };

  const handleDownload = () => {
    const url = invoiceAttachment?.download_url ?? (invoiceAttachment as { file_url?: string })?.file_url;
    if (!url) return;
    window.open(url, '_blank');
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const onRetry = async () => {
    try {
      await fetchInvoiceAttachment(invoiceId);
    } catch (err: unknown) {
      if (!isNoAttachmentResponse(err as { statusCode?: number; message?: string })) {
        showError((err as Error)?.message || 'حدث خطأ في تحميل معلومات المرفق');
      }
    }
  };

  const isForCurrentInvoice = attachmentInvoiceId === String(invoiceId);
  const hasAttachment = isForCurrentInvoice && invoiceAttachment !== null;
  const noAttachmentMessage = isNoAttachmentError(attachmentError ?? undefined);
  const showUploadAction =
    isForCurrentInvoice && !invoiceAttachment && !attachmentLoading && (!attachmentError || noAttachmentMessage);
  const displayError = noAttachmentMessage ? null : attachmentError;

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <AppSectionTitle className="font-semibold text-gray-900">مرفق الفاتورة (PDF)</AppSectionTitle>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {attachmentLoading && !hasAttachment && (
          <div className="flex flex-col items-center justify-center gap-3 py-8">
            <Spinner size={32} />
            <AppText className="text-gray-600">جاري تحميل معلومات المرفق...</AppText>
          </div>
        )}

        {displayError && !hasAttachment && !attachmentLoading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <AppErrorText>{displayError}</AppErrorText>
            </div>
            <Button variant="outline" size="small" onClick={onRetry} className="mt-2">
              إعادة المحاولة
            </Button>
          </div>
        )}

        {showUploadAction && (
          <div className="text-center py-6">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <AppText className="text-gray-600 mb-2">لا يوجد مرفق للفاتورة</AppText>
            <AppBodyText className="text-gray-500 mb-4">PDF فقط</AppBodyText>
            <Button
              variant="primary"
              onClick={triggerFileInput}
              disabled={attachmentLoading}
              isLoading={attachmentLoading}
              icon={<Upload className="h-4 w-4" />}
            >
              رفع PDF
            </Button>
          </div>
        )}

        {hasAttachment && invoiceAttachment && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <AppLabelText className="text-gray-600 mb-1">اسم الملف</AppLabelText>
                    <AppText className="font-medium text-gray-900 break-all">{invoiceAttachment.original_filename}</AppText>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <AppLabelText className="text-gray-600 mb-1">حجم الملف</AppLabelText>
                  <AppText className="font-medium">{formatFileSize(invoiceAttachment.file_size)}</AppText>
                </div>
                <div>
                  <AppLabelText className="text-gray-600 mb-1">تاريخ الرفع</AppLabelText>
                  <AppText className="font-medium">{formatDate(invoiceAttachment.uploaded_at)}</AppText>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                size="small"
                onClick={handleDownload}
                disabled={attachmentLoading}
                icon={<Download className="h-4 w-4" />}
              >
                تحميل
              </Button>
              <Button
                variant="outline"
                size="small"
                onClick={triggerFileInput}
                disabled={attachmentLoading}
                isLoading={attachmentLoading}
                icon={<RefreshCw className="h-4 w-4" />}
              >
                استبدال
              </Button>
              <Button
                variant="danger"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={attachmentLoading}
                icon={<Trash2 className="h-4 w-4" />}
              >
                حذف
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ConfirmationDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="تأكيد الحذف"
        message="هل أنت متأكد من حذف مرفق الفاتورة؟ لا يمكن التراجع عن هذا الإجراء."
        confirmText="حذف"
        cancelText="إلغاء"
        variant="danger"
        isLoading={attachmentLoading}
        confirmButtonVariant="danger"
      />
    </>
  );
};

export default InvoiceAttachmentCard;